import { createApi } from '@reduxjs/toolkit/query/react'
import { sendNotification } from 'components'
import { Coordinate, MAP_SHAPES } from 'components/MapV2/types'
import axiosBaseQuery from 'services/axiosBaseQuery'
import {
  AddAdjustmentOrderItemRequest,
  AddObjectionResponse,
  AdjustmentOrderItemDetailsResponse,
  AvailableDatesRequest,
  AvailableDatesResponseV2,
  BulkActionResponse,
  BulkAddOrderSettingsRequest,
  BulkAddOrderSettingsResponse,
  BulkDeleteOrderPlanRequest,
  BulkDeleteOrderRequest,
  CustomFieldsRequest,
  DeleteOrderPlanRequest,
  DeleteOrderPlanResponse,
  DeleteOrderRequest,
  DeleteOrderResponse,
  DynamicFieldsResponse,
  DynamicFieldsTransformedResponse,
  ExportRequest,
  GetOrderAssetTypePricesRequest,
  GetOrderAssetTypePricesResponse,
  LinkWorkOrderRequest,
  MismatchedOrderPlansType,
  MutationResponse,
  ObjectionListResponse,
  OrderAssetListRequest,
  OrderDetailRequest,
  OrderDetailResponse,
  OrderDetailTransformedResponse,
  OrderItem,
  OrderItemListRequest,
  OrderItemListResponse,
  OrderItemMappingResponse,
  OrderItemsRequest,
  OrderItemsResponse,
  OrderListRequest,
  OrderListResponse,
  OrderPlanDetailRequest,
  OrderPlanDetailResponse,
  OrderPlanDetailTransformedResponse,
  OrderPlanListRequest,
  OrderPlanListResponse,
  SearchOrderPlanResponse,
  SearchOrderRequest,
  SearchOrderResponse,
  ServiceExclusionsRequest,
  ServiceExclusionsResponse,
  TableResponseWithTimezone,
  UnlinkWorkOrderRequest,
  UpdateStatusRequest,
  WorkOrdersRequest,
  WorkOrdersRequestForDispute,
  WorkOrdersResponse,
  WorkOrdersResponseForDispute,
} from 'services/Engagement/types'
import { EngagementEnvironmentResponse } from 'services/environment'
import { consignmentApi } from 'services/MrfManagement/consignment'
import { inboundApi } from 'services/MrfManagement/inbound'
import { parcelApi } from 'services/MrfManagement/parcel'
import { taskApi } from 'services/OpsManagement'
import { reportsApi } from 'services/ReportManagement/reports'
import { TableRequestV2, TableResponseV2 } from 'services/types'
import { DropdownOptionTemplate } from 'types/common'
import { sendExportNotification, sendImportNotification } from 'utils'
import i18n from 'utils/i18n'
import { entityApi } from './entity'
import { servicePointApi } from './servicePoint'

export const orderApi = createApi({
  reducerPath: 'engagementOrderApi',
  tagTypes: [
    'Orders',
    'OrderPlans',
    'OrderDetails',
    'OrderPlanDetails',
    'OrderSearch',
    'WorkOrders',
    'ObjectionList',
    'OrderItems',
    'OrderHistory',
    'AdjustmentOrderItemDetail',
    'ServiceAvailability',
    'OrderCalendar',
    'OrderSettings',
    'OrderItemsTable',
    'WorkOrdersForDispute',
    'MismatchedOrderPlans',
    'AvailableDatesV2',
    'OrderAssetsTable',
    'OrderAssetTypePrices',
  ],
  baseQuery: axiosBaseQuery({
    baseUrl: `${window.envVariables.REACT_APP_BASE_URL + '/engagement/'}`,
  }),
  endpoints: (builder) => ({
    addOrder: builder.mutation<
      MutationResponse & { order_id: string },
      { client_id: string; body: FormData }
    >({
      query: ({ client_id, body }) => ({
        url: `clients/${client_id}/order/`,
        method: 'POST',
        data: body,
      }),
      invalidatesTags: ['Orders', 'OrderHistory', 'OrderCalendar'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
            hyperLink: {
              type: 'newTab',
              label: i18n.t('response.see_more'),
              to: `/engagement/orders/detail/${data.order_id}`,
            },
          })
          dispatch(entityApi.util.invalidateTags(['ServiceDays', 'ServiceDayDetails']))
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    editOrder: builder.mutation<
      MutationResponse,
      { client_id: string; order_id: string; body: FormData }
    >({
      query: ({ client_id, order_id, body }) => ({
        url: `clients/${client_id}/order/${order_id}/`,
        method: 'POST',
        data: body,
      }),
      invalidatesTags: ['Orders', 'OrderDetails', 'OrderHistory', 'OrderCalendar', 'OrderSettings'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    editOrderPlan: builder.mutation<
      MutationResponse & { order_plan_id: string },
      { client_id: string; order_plan_id: string; body: FormData }
    >({
      query: ({ client_id, order_plan_id, body }) => ({
        url: `clients/${client_id}/order_plan/${order_plan_id}/`,
        method: 'POST',
        data: body,
      }),
      invalidatesTags: [
        'OrderPlans',
        'OrderPlanDetails',
        'Orders',
        'OrderItems',
        'OrderCalendar',
        'MismatchedOrderPlans',
      ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
            hyperLink: {
              type: 'newTab',
              label: i18n.t('response.see_more'),
              to: `/engagement/order_plans/detail/${data.order_plan_id}`,
            },
          })
          dispatch(entityApi.util.invalidateTags(['ServiceDays', 'ServiceDayDetails']))
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    addOrderPlan: builder.mutation<
      MutationResponse & { order_plan_id: string },
      { client_id: string; body: FormData }
    >({
      query: ({ client_id, body }) => ({
        url: `clients/${client_id}/order_plan/`,
        method: 'POST',
        data: body,
      }),
      invalidatesTags: ['OrderPlans', 'OrderCalendar', 'MismatchedOrderPlans'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
            hyperLink: {
              type: 'newTab',
              label: i18n.t('response.see_more'),
              to: `/engagement/order_plans/detail/${data.order_plan_id}`,
            },
          })
          dispatch(entityApi.util.invalidateTags(['ServiceDays', 'ServiceDayDetails']))
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    getOrders: builder.query<OrderListResponse, OrderListRequest>({
      query: ({ client_id, task_id, ...data }) => ({
        url: `clients/${client_id}/order/list/`,
        method: 'POST',
        data,
        params: {
          task_id,
        },
      }),
      providesTags: ['Orders'],
    }),
    getOrderPlans: builder.query<OrderPlanListResponse, OrderPlanListRequest>({
      query: ({ client_id, ...data }) => ({
        url: `clients/${client_id}/order_plan/list/`,
        method: 'POST',
        data,
      }),
      providesTags: ['OrderPlans'],
    }),
    getOrderItems: builder.query<OrderItemsResponse, OrderItemsRequest>({
      query: ({ client_id, list_order_items_type, list_order_items, ...data }) => ({
        url: `clients/${client_id}/${list_order_items_type}/${list_order_items}/`,
        method: 'POST',
        data,
      }),
      providesTags: ['OrderItemsTable'],
    }),
    getOrderAssetsRecords: builder.query<
      TableResponseV2<{}> & {
        timezone: string
      },
      OrderAssetListRequest
    >({
      query: ({ clientId, ...data }) => ({
        url: `clients/${clientId}/order_asset_records/`,
        method: 'POST',
        data,
      }),
      providesTags: ['OrderAssetsTable'],
    }),
    getOrderItemList: builder.query<OrderItemListResponse, OrderItemListRequest>({
      query: ({ client_id, ...data }) => ({
        url: `clients/${client_id}/order_item/list/`,
        method: 'POST',
        data,
      }),
      providesTags: ['OrderItems'],
    }),
    getOrderDetails: builder.query<OrderDetailTransformedResponse, OrderDetailRequest>({
      query: ({ clientId, orderId }) => ({
        url: `clients/${clientId}/order/`,
        method: 'GET',
        params: {
          order_id: orderId,
        },
      }),
      providesTags: ['OrderDetails'],
      transformResponse: ({
        location: { address, coordinates, type },
        ...rest
      }: OrderDetailResponse) => ({
        location: {
          coordinates: coordinates as Coordinate[],
          address: address as string,
          type: type.toLowerCase() as (typeof MAP_SHAPES)['single'],
        },
        ...rest,
      }),
    }),
    getOrderPlanDetails: builder.query<OrderPlanDetailTransformedResponse, OrderPlanDetailRequest>({
      query: ({ client_id, orderPlanId }) => ({
        url: `clients/${client_id}/order_plan/`,
        method: 'GET',
        params: {
          order_plan_id: orderPlanId,
        },
      }),
      providesTags: ['OrderPlanDetails'],
      transformResponse: ({
        location: { address, coordinates, type },
        ...rest
      }: OrderPlanDetailResponse) => ({
        location: {
          coordinates: coordinates as Coordinate[],
          address: address as string,
          type: type.toLowerCase() as (typeof MAP_SHAPES)['single'],
        },
        ...rest,
      }),
    }),
    deleteOrder: builder.mutation<DeleteOrderResponse, DeleteOrderRequest>({
      query: ({ client_id, order_id }) => ({
        url: `clients/${client_id}/order/${order_id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Orders', 'OrderPlanDetails', 'OrderCalendar', 'ServiceAvailability'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
          dispatch(
            entityApi.util.invalidateTags([
              'ServiceDays',
              'ServiceDayDetails',
              'EntityOrdersExternal',
            ]),
          )
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    deleteOrderPlan: builder.mutation<DeleteOrderPlanResponse, DeleteOrderPlanRequest>({
      query: ({ client_id, order_plan_id, ...data }) => ({
        url: `clients/${client_id}/order_plan/${order_plan_id}/`,
        data: data,
        method: 'DELETE',
      }),
      invalidatesTags: [
        'OrderPlans',
        'OrderCalendar',
        'Orders',
        'ServiceAvailability',
        'OrderPlanDetails',
        'MismatchedOrderPlans',
      ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
          dispatch(entityApi.util.invalidateTags(['ServiceDays', 'ServiceDayDetails']))
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    searchOrder: builder.mutation<SearchOrderResponse, SearchOrderRequest>({
      query: ({ client_id, ...data }) => ({
        url: `clients/${client_id}/order/search/`,
        method: 'POST',
        data,
      }),
    }),
    searchOrderPlan: builder.mutation<SearchOrderPlanResponse, SearchOrderRequest>({
      query: ({ client_id, ...data }) => ({
        url: `clients/${client_id}/order_plan/search/`,
        method: 'POST',
        data,
      }),
    }),
    bulkDeleteOrder: builder.mutation<BulkActionResponse, BulkDeleteOrderRequest>({
      query: ({ client_id, ...data }) => ({
        url: `clients/${client_id}/order/bulk_delete/`,
        method: 'DELETE',
        data,
      }),
      invalidatesTags: [
        'Orders',
        'OrderDetails',
        'OrderPlanDetails',
        'OrderCalendar',
        'ServiceAvailability',
      ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          if (data.failure_list.length === 0) {
            sendNotification({
              type: 'success',
              toastContent: data.detail.message,
            })
          } else if (data.successful_list.length === 0) {
            sendNotification({
              type: 'error',
              toastContent: data.detail.message,
            })
          } else {
            let failure_list_items = data.detail.message
            data.failure_list.forEach((item) => (failure_list_items += item.description))
            sendNotification({
              type: 'warning',
              toastContent: failure_list_items,
            })
          }
          dispatch(
            entityApi.util.invalidateTags([
              'ServiceDays',
              'ServiceDayDetails',
              'EntityOrdersExternal',
            ]),
          )
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    bulkDeleteOrderPlan: builder.mutation<BulkActionResponse, BulkDeleteOrderPlanRequest>({
      query: ({ client_id, ...data }) => ({
        url: `clients/${client_id}/order_plan/bulk_delete/`,
        method: 'DELETE',
        data,
      }),
      invalidatesTags: [
        'OrderPlans',
        'OrderCalendar',
        'Orders',
        'ServiceAvailability',
        'OrderPlanDetails',
      ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          if (data.failure_list.length === 0) {
            sendNotification({
              type: 'success',
              toastContent: data.detail.message,
            })
          } else if (data.successful_list.length === 0) {
            sendNotification({
              type: 'error',
              toastContent: data.detail.message,
            })
          } else {
            let failure_list_items = data.detail.message
            data.failure_list.forEach((item) => (failure_list_items += item.description))
            sendNotification({
              type: 'warning',
              toastContent: failure_list_items,
            })
          }

          dispatch(entityApi.util.invalidateTags(['ServiceDays', 'ServiceDayDetails']))

          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    exportOrder: builder.mutation<MutationResponse, ExportRequest>({
      query: ({ client_id, ...data }) => ({
        url: `clients/${client_id}/order/export/`,
        method: 'POST',
        data,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          sendExportNotification(data.detail.message)
          dispatch(reportsApi.util.invalidateTags(['ExportReports']))
        } catch (error) {}
      },
    }),
    exportOrderPlan: builder.mutation<MutationResponse, ExportRequest>({
      query: ({ client_id, ...data }) => ({
        url: `clients/${client_id}/order_plan/export/`,
        method: 'POST',
        data,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          sendExportNotification(data.detail.message)
          dispatch(reportsApi.util.invalidateTags(['ExportReports']))
        } catch (error) {}
      },
    }),
    getOrderDynamicFields: builder.query<DynamicFieldsTransformedResponse, CustomFieldsRequest>({
      query: ({ clientId, type_id }) => ({
        url: `clients/${clientId}/order_template_dynamic_fields_by_order_type/?order_type=${type_id}`,
        method: 'GET',
      }),
      transformResponse: ({ order_template_content_fields: data }: DynamicFieldsResponse) => {
        return data.map(({ field_type, ...rest }) => ({
          type: field_type,
          ...rest,
        }))
      },
    }),
    getWorkOrders: builder.query<WorkOrdersResponse, WorkOrdersRequest>({
      query: ({ client_id, ...data }) => ({
        url: `clients/${client_id}/work_order/list/`,
        method: 'POST',
        data,
      }),
      providesTags: ['WorkOrders'],
    }),
    linkWorkOrder: builder.mutation<MutationResponse, LinkWorkOrderRequest>({
      query: ({ client_id, ...data }) => ({
        url: `clients/${client_id}/work_order/create/`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['WorkOrders', 'OrderDetails', 'OrderItemsTable', 'WorkOrdersForDispute'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
          setTimeout(() => {
            dispatch(parcelApi.util.invalidateTags(['WorkOrderListByOrderId']))
            dispatch(taskApi.util.invalidateTags(['Tasks']))
            dispatch(consignmentApi.util.invalidateTags(['WorkOrderListByOrderId']))
            dispatch(inboundApi.util.invalidateTags(['WorkOrderListByOrderId']))
          }, 1000)
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    unlinkWorkOrder: builder.mutation<MutationResponse, UnlinkWorkOrderRequest>({
      query: ({ client_id, ...data }) => ({
        url: `clients/${client_id}/work_order/unlink/`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['WorkOrders', 'OrderDetails', 'OrderItemsTable', 'WorkOrdersForDispute'],

      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
          setTimeout(() => {
            dispatch(parcelApi.util.invalidateTags(['WorkOrderListByOrderId']))
            dispatch(taskApi.util.invalidateTags(['Tasks']))
            dispatch(consignmentApi.util.invalidateTags(['WorkOrderListByOrderId']))
            dispatch(inboundApi.util.invalidateTags(['WorkOrderListByOrderId']))
          }, 1000)
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    getWorkOrderForDispute: builder.query<
      WorkOrdersResponseForDispute,
      WorkOrdersRequestForDispute
    >({
      query: ({ client_id, order_id }) => ({
        url: `clients/${client_id}/order/${order_id}/list/work_orders/`,
        method: 'POST',
      }),
      providesTags: ['WorkOrdersForDispute'],
    }),
    addObjection: builder.mutation<
      AddObjectionResponse,
      {
        client_id: string
        body: FormData
      }
    >({
      query: ({ client_id, body }) => ({
        url: `clients/${client_id}/objection/create/`,
        method: 'POST',
        data: body,
      }),
      invalidatesTags: ['OrderDetails', 'ObjectionList', 'Orders'],
    }),
    getObjectionList: builder.query<ObjectionListResponse, { client_id: string; order_id: string }>(
      {
        query: ({ client_id, order_id }) => ({
          url: `clients/${client_id}/objection/list/?order_id=${order_id}`,
          method: 'GET',
        }),
        providesTags: ['ObjectionList'],
      },
    ),
    importOrders: builder.mutation<MutationResponse, { clientId: string; body: FormData }>({
      query: ({ clientId, body }) => ({
        url: `clients/${clientId}/order/import/`,
        method: 'POST',
        data: body,
      }),
      invalidatesTags: ['Orders'],
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          sendImportNotification(data.detail.message)
          dispatch(reportsApi.util.invalidateTags(['ImportReports']))
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    importOrderPlans: builder.mutation<MutationResponse, { clientId: string; body: FormData }>({
      query: ({ clientId, body }) => ({
        url: `clients/${clientId}/order_plan/import/`,
        method: 'POST',
        data: body,
      }),
      invalidatesTags: ['OrderPlans'],
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          sendImportNotification(data.detail.message)
          dispatch(reportsApi.util.invalidateTags(['ImportReports']))
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    orderHistory: builder.query<
      TableResponseWithTimezone<{}>,
      TableRequestV2 & {
        order_id: string
      }
    >({
      query: ({ client_id, order_id, ...data }) => ({
        url: `clients/${client_id}/order/${order_id}/history/`,
        method: 'POST',
        data,
      }),
      providesTags: ['OrderHistory'],
    }),
    updateOrderStatus: builder.mutation<MutationResponse, UpdateStatusRequest>({
      query: ({ client_id, ...data }) => ({
        url: `clients/${client_id}/status/update/`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Orders', 'OrderDetails', 'OrderPlans', 'OrderPlanDetails', 'OrderHistory'],
    }),
    addAdjustmentOrderItem: builder.mutation<MutationResponse, AddAdjustmentOrderItemRequest>({
      query: ({ client_id, ...data }) => ({
        url: `clients/${client_id}/order_item_record/`,
        method: 'POST',
        data: data,
      }),
      invalidatesTags: ['OrderItems', 'OrderDetails', 'OrderItemsTable'],
    }),
    editAdjustmentOrderItem: builder.mutation<MutationResponse, AddAdjustmentOrderItemRequest>({
      query: ({ client_id, ...data }) => ({
        url: `clients/${client_id}/order_item_record/${data.order_item_id}/`,
        method: 'POST',
        data: data,
      }),
      invalidatesTags: [
        'OrderItems',
        'AdjustmentOrderItemDetail',
        'OrderDetails',
        'OrderItemsTable',
      ],
    }),
    deleteAdjustmentOrderItem: builder.mutation<
      MutationResponse,
      { order_item_id: string; client_id: string }
    >({
      query: ({ client_id, order_item_id }) => ({
        url: `clients/${client_id}/order_item_record/${order_item_id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [
        'OrderItems',
        'AdjustmentOrderItemDetail',
        'OrderDetails',
        'OrderItemsTable',
      ],
    }),
    getAdjustmentOrderItemDetail: builder.query<
      AdjustmentOrderItemDetailsResponse,
      { client_id: string; order_item_id: string }
    >({
      query: ({ client_id, order_item_id }) => ({
        url: `clients/${client_id}/order_item_record/?order_item_record_id=${order_item_id}`,
        method: 'GET',
      }),
      providesTags: ['AdjustmentOrderItemDetail'],
    }),
    getOrderItemMapping: builder.query<
      OrderItemMappingResponse,
      { client_id: string; order_type_id: string }
    >({
      query: ({ client_id, order_type_id }) => ({
        url: `clients/${client_id}/order_item/list_external_items/`,
        method: 'POST',
        data: {
          order_type_id,
        },
      }),
    }),
    getMismatchedOrderPlans: builder.query<
      { detail: { message: string }; order_plans: MismatchedOrderPlansType },
      {
        client_id: string
        service_point_id?: string
        entity_id?: string
        order_settings: Array<{ order_type: string; order_items: Array<string> }>
      }
    >({
      query: ({ client_id, ...data }) => ({
        url: `clients/${client_id}/order_plan/check_mismatched_order_plans/`,
        method: 'POST',
        data,
      }),
      providesTags: ['MismatchedOrderPlans'],
    }),
    updateOrderNote: builder.mutation<
      MutationResponse,
      { clientId: string; orderId: string; note?: string }
    >({
      query: ({ clientId, orderId, ...data }) => ({
        url: `clients/${clientId}/order/${orderId}/note/`,
        method: 'POST',
        data,
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
      invalidatesTags: ['Orders', 'OrderDetails'],
    }),
    addExclusion: builder.mutation<
      MutationResponse,
      {
        client_id: string
        body: {
          name: string
          entity_id?: string
          order_type_id?: string
          service_point_id?: string
          date_ranges?: Array<{ start: string; end: string }>
          dates?: Array<string>
        }
      }
    >({
      query: ({ client_id, body }) => ({
        url: `clients/${client_id}/service_exclusion/`,
        method: 'POST',
        data: body,
      }),
      invalidatesTags: ['ServiceAvailability', 'AvailableDatesV2'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    deleteExclusion: builder.mutation<
      MutationResponse,
      {
        client_id: string
        exclusion_id: string
      }
    >({
      query: ({ client_id, exclusion_id }) => ({
        url: `clients/${client_id}/service_exclusion/${exclusion_id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ServiceAvailability', 'AvailableDatesV2'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    editExclusion: builder.mutation<
      MutationResponse,
      {
        client_id: string
        exclusion_id: string
        body: {
          name: string
          entity_id?: string
          order_type_id?: string
          service_point_id?: string
          date_ranges?: Array<{ start: string; end: string }>
          dates?: Array<string>
          deleted_date_ids?: Array<string>
        }
      }
    >({
      query: ({ client_id, exclusion_id, body }) => ({
        url: `clients/${client_id}/service_exclusion/${exclusion_id}/`,
        method: 'PUT',
        data: body,
      }),
      invalidatesTags: ['ServiceAvailability', 'AvailableDatesV2'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    serviceExclusions: builder.query<ServiceExclusionsResponse, ServiceExclusionsRequest>({
      query: ({ client_id, ...rest }) => ({
        url: `clients/${client_id}/service_exclusion/exclusions/`,
        method: 'POST',
        data: rest,
      }),
      providesTags: ['ServiceAvailability'],
    }),
    availableDatesV2: builder.query<AvailableDatesResponseV2, AvailableDatesRequest>({
      query: ({ client_id, ...rest }) => ({
        url: `clients/${client_id}/service_exclusion/available_dates_v2/`,
        method: 'POST',
        data: rest,
      }),
      providesTags: ['AvailableDatesV2'],
    }),
    orderCalendarList: builder.query<
      {
        orders: Array<{
          date: string
          orders: Array<DropdownOptionTemplate<string>[0] & { is_planned: boolean }>
        }>
      },
      ServiceExclusionsRequest
    >({
      query: ({ client_id, ...rest }) => ({
        url: `clients/${client_id}/order/monthly_order_list/`,
        method: 'POST',
        data: rest,
      }),
      providesTags: ['OrderCalendar'],
    }),
    addBulkOrder: builder.mutation<MutationResponse, { client_id: string; body: FormData }>({
      query: ({ client_id, body }) => ({
        url: `clients/${client_id}/order/bulk_create/`,
        method: 'POST',
        data: body,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
          dispatch(
            entityApi.util.invalidateTags([
              'ServiceDays',
              'ServiceDayDetails',
              'EntityOrdersExternal',
            ]),
          )
        } catch (err) {
          // `onError` side-effect
        }
      },
      invalidatesTags: ['OrderCalendar', 'Orders'],
    }),
    getOrderSettings: builder.query<
      {
        order_types: EngagementEnvironmentResponse['order_types']
        order_items: Array<OrderItem & { order_type_id: string }>
      },
      {
        client_id: string
        service_point_id?: string | string[]
        entity_id?: string
      }
    >({
      query: ({ client_id, entity_id, service_point_id }) => ({
        url: `clients/${client_id}/order_settings/`,
        method: 'GET',
        params: {
          entity_id,
          service_point_id: Array.isArray(service_point_id)
            ? service_point_id.join(',')
            : service_point_id || undefined,
        },
      }),
      providesTags: ['OrderSettings'],
    }),
    bulkAddOrderSettings: builder.mutation<
      BulkAddOrderSettingsResponse,
      BulkAddOrderSettingsRequest
    >({
      query: ({ client_id, ...data }) => ({
        url: `clients/${client_id}/order_settings/bulk_add/`,
        method: 'POST',
        data,
      }),
      async onQueryStarted({ model }, { queryFulfilled, dispatch }) {
        await queryFulfilled

        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
          if (model === 'entity') {
            dispatch(entityApi.util.invalidateTags(['Entities', 'EntityDetail']))
          }
          if (model === 'service_point') {
            dispatch(servicePointApi.util.invalidateTags(['ServicePoints', 'ServicePointDetail']))
          }
        } catch {}
      },
      invalidatesTags: ['OrderSettings', 'Orders', 'ServiceAvailability'],
    }),
    getOrderAssetTypePrices: builder.query<
      GetOrderAssetTypePricesResponse,
      GetOrderAssetTypePricesRequest
    >({
      query: ({ client_id, ...data }) => ({
        url: `clients/${client_id}/order_asset_types/`,
        method: 'POST',
        data,
      }),
      transformResponse: ({ data, detail }: GetOrderAssetTypePricesResponse) => {
        return {
          detail,
          data: data.map(({ price, market_share, tax, ...rest }) => ({
            ...rest,
            price: Number(price),
            market_share: Number(market_share),
            tax: Number(tax),
          })),
        }
      },
      providesTags: ['OrderAssetTypePrices'],
    }),
    getOrderTypeOperationMapping: builder.query<
      {
        mapping_list: Record<
          string,
          Array<{ order_type_id: string; order_type_name: string; mapping_id: string }>
        >
      },
      { client_id: string }
    >({
      query: ({ client_id }) => ({
        url: `clients/${client_id}/order_type_operation_mappings/`,
        method: 'GET',
      }),
    }),
  }),
})

export const {
  useGetOrdersQuery,
  useGetOrderPlansQuery,
  useDeleteOrderMutation,
  useDeleteOrderPlanMutation,
  useAddOrderMutation,
  useEditOrderMutation,
  useAddOrderPlanMutation,
  useGetOrderDetailsQuery,
  useGetOrderItemsQuery,
  useBulkDeleteOrderMutation,
  useBulkDeleteOrderPlanMutation,
  useGetOrderPlanDetailsQuery,
  useSearchOrderMutation,
  useEditOrderPlanMutation,
  useSearchOrderPlanMutation,
  useExportOrderMutation,
  useExportOrderPlanMutation,
  useGetOrderDynamicFieldsQuery,
  useGetWorkOrdersQuery,
  useLinkWorkOrderMutation,
  useGetOrderItemListQuery,
  useGetWorkOrderForDisputeQuery,
  useAddObjectionMutation,
  useGetObjectionListQuery,
  useUnlinkWorkOrderMutation,
  useImportOrdersMutation,
  useImportOrderPlansMutation,
  useOrderHistoryQuery,
  useUpdateOrderStatusMutation,
  useAddAdjustmentOrderItemMutation,
  useEditAdjustmentOrderItemMutation,
  useDeleteAdjustmentOrderItemMutation,
  useGetAdjustmentOrderItemDetailQuery,
  useGetOrderItemMappingQuery,
  useGetMismatchedOrderPlansQuery,
  useUpdateOrderNoteMutation,
  useAddBulkOrderMutation,
  useAddExclusionMutation,
  useServiceExclusionsQuery,
  useOrderCalendarListQuery,
  useDeleteExclusionMutation,
  useEditExclusionMutation,
  useGetOrderSettingsQuery,
  useLazyGetMismatchedOrderPlansQuery,
  useAvailableDatesV2Query,
  useLazyGetOrderItemListQuery,
  useBulkAddOrderSettingsMutation,
  useGetOrderAssetTypePricesQuery,
  useGetOrderTypeOperationMappingQuery,
} = orderApi
